import { Link } from "react-router-dom";
import { LOGO_URL } from "@/lib/gateways";
import useSEO from "@/hooks/useSEO";
import MinisterCredential from "@/components/MinisterCredential";

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2
      className="font-sans-body text-[10px] tracking-[0.36em] uppercase mb-3"
      style={{ color: "var(--olive)" }}
    >
      {title}
    </h2>
    <div
      className="font-sans-body text-[14px] leading-relaxed space-y-4"
      style={{ color: "var(--ink)" }}
    >
      {children}
    </div>
  </section>
);

const TermsAndConditions = () => {
  const effective = "December 2025";

  useSEO({
    title: "Terms & Conditions — Selah & Stone Legacy Holdings",
    description:
      "The terms governing your access to Selah and Stone Legacy Holdings. Read our full terms and conditions.",
    path: "/terms",
  });

  return (
    <div className="paper-grain relative min-h-screen" data-testid="terms-page">
      <div className="relative z-10">
        {/* Top bar */}
        <div className="px-6 sm:px-10 pt-8">
          <div className="mx-auto max-w-3xl flex items-start justify-between">
            <Link to="/" className="leading-tight">
              <div
                className="font-sans-body text-[10px] sm:text-[11px] tracking-[0.32em] uppercase"
                style={{ color: "var(--ink)" }}
              >
                Selah &nbsp;&middot;&nbsp; Stone
              </div>
              <div
                className="font-sans-body text-[9px] sm:text-[10px] tracking-[0.42em] uppercase mt-1"
                style={{ color: "var(--olive)" }}
              >
                Legacy Holdings
              </div>
            </Link>
            <Link
              to="/"
              className="font-sans-body text-[11px] tracking-[0.22em] uppercase pt-1 underline hover:no-underline"
              style={{ color: "var(--stone-deep)" }}
              data-testid="terms-back-link"
            >
              Back
            </Link>
          </div>
        </div>

        {/* Header */}
        <header className="px-6 sm:px-10 pt-16 pb-10">
          <div className="mx-auto max-w-3xl">
            <img
              src={LOGO_URL}
              alt="Selah and Stone Legacy Holdings"
              className="h-16 w-16 mb-6"
            />
            <p
              className="font-sans-body text-[10px] tracking-[0.42em] uppercase"
              style={{ color: "var(--olive)" }}
            >
              Terms &amp; Conditions
            </p>
            <h1
              className="mt-3 font-serif-display text-5xl sm:text-6xl font-light leading-[1]"
              style={{ color: "var(--ink)" }}
            >
              The terms of our
              <span
                className="block italic"
                style={{ color: "var(--olive)" }}
              >
                shared threshold.
              </span>
            </h1>
            <p
              className="mt-6 font-serif-display italic text-xl"
              style={{ color: "var(--stone-deep)" }}
            >
              Effective {effective}
            </p>
          </div>
        </header>

        {/* Body */}
        <article className="px-6 sm:px-10 pb-20">
          <div className="mx-auto max-w-3xl">
            <p
              className="font-serif-display text-xl leading-relaxed italic"
              style={{ color: "var(--stone-deep)" }}
            >
              These Terms &amp; Conditions (the &ldquo;Terms&rdquo;) govern
              your access to and use of selahandstonelegacy.com and any
              related services offered by Selah and Stone Legacy Holdings,
              LLC (&ldquo;Selah and Stone,&rdquo; &ldquo;we,&rdquo;
              &ldquo;our,&rdquo; or &ldquo;us&rdquo;). By using this site or
              submitting any form, you agree to these Terms.
            </p>

            <Section title="1. Who we are">
              <p>
                Selah and Stone Legacy Holdings, LLC is a United States
                family legacy and forensic firm. Our family of brands
                currently includes{" "}
                <strong className="font-semibold">Selah Slate</strong>,{" "}
                <strong className="font-semibold">Selah Downloads</strong>,{" "}
                <strong className="font-semibold">Selah and Stone Research</strong>,{" "}
                <strong className="font-semibold">Selah and Salt Home</strong>,{" "}
                <strong className="font-semibold">Family Legacy Audits</strong>, and{" "}
                <strong className="font-semibold">Omni Signing Services</strong>,
                together with the forthcoming book,{" "}
                <em>The Selah Shift</em>.
              </p>
              <p>
                We are not affiliated with any textile or home goods
                companies outside of our family of brands.
              </p>
            </Section>

            <Section title="2. Use of the site">
              <p>
                You agree to use the site only for lawful purposes and in a
                manner that does not infringe the rights of, or restrict or
                inhibit the use of the site by, any third party. You may
                not attempt to gain unauthorized access to any portion of
                the site, interfere with its operation, or scrape data
                without our express written permission.
              </p>
            </Section>

            <Section title="3. Not professional advice">
              <p>
                Information on this site, in our emails, and in our books is
                provided for general informational and inspirational
                purposes only. Nothing on this site constitutes legal,
                financial, tax, mortgage, medical, therapeutic, or
                professional advice.
              </p>
              <p>
                <strong className="font-semibold">Selah Slate<sup className="tm">TM</sup></strong>{" "}
                is a financial wellness initiative. We are not a law firm or
                credit repair clinic, and results depend on the user's active
                participation.
              </p>
              <p>
                <strong className="font-semibold">Selah Downloads<sup className="tm">TM</sup></strong>{" "}
                offers spiritual guidance content and is not a substitute for
                professional counseling, therapy, or mental health care.
              </p>
              <p>
                <strong className="font-semibold">Selah and Stone Research<sup className="tm">TM</sup></strong>{" "}
                performs background screening and open-source forensic due
                diligence. We are not a licensed private investigative firm.
                All engagements are governed by a separate service agreement
                and performed in accordance with applicable law, including
                the Fair Credit Reporting Act (FCRA) where relevant.
              </p>
              <p>
                <strong className="font-semibold">Omni Signing Services<sup className="tm">TM</sup></strong>{" "}
                provides notary public and loan signing services only.
                Notaries and signing agents may not select forms, draft
                documents, or explain the contents or legal implications of
                documents signed. Any future mortgage origination services
                will be offered exclusively through a properly licensed
                entity in compliance with applicable federal and state law.
              </p>
              <p>
                Always consult a qualified professional before acting on
                information received from any of our brands.
              </p>
            </Section>

            <Section title="4. Waitlists, pre-orders, and beta access">
              <p>
                Joining a waitlist does not guarantee access to a product
                or service, a specific launch date, a price, or any other
                commercial terms. We may alter, delay, or discontinue any
                waitlist, beta, or pre-order at our discretion. If fees are
                ever collected in connection with a product or service, the
                applicable terms and any refund policy will be disclosed at
                the point of purchase and will supplement these Terms.
              </p>
            </Section>

            <Section title="5. Accounts and credentials">
              <p>
                Certain areas of the site are restricted to authorized
                administrators. You are responsible for keeping any password
                or credential you receive confidential and for all activity
                conducted under it. We reserve the right to revoke access at
                any time.
              </p>
            </Section>

            <Section title="6. Intellectual property">
              <p>
                All content on this site — including the Selah and Stone
                seal, brand marks, book titles, wordmarks, copy, images,
                and design — is owned by Selah and Stone Legacy Holdings,
                LLC or used with permission, and is protected by United
                States and international intellectual property law.
              </p>
              <p>
                Selah Slate<sup className="tm">TM</sup>, Selah Downloads
                <sup className="tm">TM</sup>, Selah and Stone Research
                <sup className="tm">TM</sup>, Selah and Salt Home
                <sup className="tm">TM</sup>, Family Legacy Audits
                <sup className="tm">TM</sup>, Omni Signing Services
                <sup className="tm">TM</sup>, and The Selah Shift
                <sup className="tm">TM</sup> are trademarks of Selah and
                Stone Legacy Holdings, LLC. You may not use them without
                our prior written permission.
              </p>
            </Section>

            <Section title="7. Third-party links">
              <p>
                The site may link to third-party websites or services
                (including omnisigningservices.com and email/analytics
                providers). We do not control and are not responsible for
                the content, policies, or practices of those third parties.
                Access them at your own discretion.
              </p>
            </Section>

            <Section title="8. Disclaimer of warranties">
              <p>
                The site and services are provided on an &ldquo;as is&rdquo;
                and &ldquo;as available&rdquo; basis. To the fullest extent
                permitted by law, we disclaim all warranties, express or
                implied, including warranties of merchantability, fitness
                for a particular purpose, non-infringement, uninterrupted
                access, and freedom from error or harmful components.
              </p>
            </Section>

            <Section title="9. Limitation of liability">
              <p>
                To the fullest extent permitted by law, Selah and Stone
                Legacy Holdings, LLC, its members, officers, employees, and
                contractors will not be liable for any indirect, incidental,
                consequential, special, punitive, or exemplary damages, or
                for any loss of profits, revenue, data, or goodwill arising
                out of or related to your use of the site or services. Our
                aggregate liability for any claim arising under these Terms
                will not exceed the greater of (a) the amount you paid us
                in the twelve (12) months preceding the claim or (b) one
                hundred U.S. dollars ($100).
              </p>
            </Section>

            <Section title="10. Indemnification">
              <p>
                You agree to indemnify and hold harmless Selah and Stone
                Legacy Holdings, LLC and its affiliates from and against any
                claims, damages, losses, liabilities, and expenses
                (including reasonable attorneys' fees) arising out of your
                breach of these Terms, your misuse of the site, or your
                violation of any law or the rights of a third party.
              </p>
            </Section>

            <Section title="11. Governing law and venue">
              <p>
                These Terms are governed by the laws of the State of
                Florida, without regard to its conflict-of-law principles.
                Any dispute arising out of or relating to these Terms or
                the site will be brought exclusively in the state or
                federal courts located in Hillsborough County, Florida, and
                you consent to the personal jurisdiction of those courts.
              </p>
            </Section>

            <Section title="12. Changes to these Terms">
              <p>
                We may update these Terms from time to time. The most
                current version will always be posted at this URL with an
                updated effective date. Your continued use of the site
                after the changes take effect constitutes your acceptance
                of the revised Terms.
              </p>
            </Section>

            <Section title="13. Contact">
              <p>
                Questions about these Terms? Write to us at{" "}
                <a
                  href="mailto:admin@selahandstonelegacy.com"
                  className="underline"
                  style={{ color: "var(--olive)" }}
                  data-testid="terms-contact-link"
                >
                  admin@selahandstonelegacy.com
                </a>
                .
              </p>
              <p className="pt-2 font-serif-display italic" style={{ color: "var(--stone-deep)" }}>
                Selah and Stone Legacy Holdings, LLC
                <br />
                Registered in Hillsborough County, Florida, USA
              </p>
            </Section>

            <div className="rule-ornament mt-16">
              <span
                className="font-serif-display italic text-sm"
                style={{ color: "var(--stone-deep)" }}
              >
                selah
              </span>
            </div>

            <MinisterCredential />
          </div>
        </article>
      </div>
    </div>
  );
};

export default TermsAndConditions;
